/**
 * Central "what do you want to do with this file?" sheet that fires
 * after any in-app exporter (notes → PDF/Markdown, tactics/whiteboard
 * → PDF, …) produces bytes. Replaces the legacy "always download"
 * behaviour so the user can pick between:
 *
 *   - Herunterladen        — save to disk (Tauri save-as / browser download)
 *   - In Medien speichern  — push into the media library (lokal or cloud)
 *   - Teilen               — Web Share API (when supported)
 *   - Drucken              — hand off to the central print module
 *                            (only for image/PDF payloads)
 *
 * The sheet is exposed via `useExportSheet()` which returns
 * `{ openExportSheet, sheet }`. Hosts render `{sheet}` once in their
 * tree; `openExportSheet(payload)` resolves once the user picks an
 * action and it completes (or the sheet is dismissed).
 */

import { useQueryClient } from "@tanstack/react-query";
import { Cloud, Download, FileText, HardDrive, Loader2, Printer, Share2, X } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import { useCallback, useMemo, useRef, useState } from "react";
import { apiFetch } from "../../lib/api.js";
import {
  importLocalMediaFromBytes,
  isLocalMediaAvailable,
  isTauri,
} from "../../lib/local-media.js";
import { useSync } from "../../providers/sync-provider";
import { isShareAvailable, shareFile } from "../media/export.js";
import { usePrint } from "../print/index.js";

/* ─────────────────────────── types ─────────────────────────── */

export interface ExportSheetPayload {
  /** Generated file bytes (PDF, PNG, Markdown, …). */
  bytes: Uint8Array;
  /** Suggested filename incl. extension. */
  filename: string;
  /** MIME type — drives the print action + Tauri filter list. */
  mime: string;
  /**
   * Optional title shown in the sheet header. Defaults to the filename.
   * Useful for "Spielzug exportieren" vs the literal filename.
   */
  title?: string;
}

/* ─────────────────────── helper: cloud upload ───────────────── */

interface PresignResponse {
  url: string;
  s3Key: string;
  headers: Record<string, string>;
  publicUrl: string;
  expiresIn: number;
}

function stripExtension(name: string): string {
  return name.replace(/\.[^./\\]+$/, "");
}

async function uploadBytesToCloud(args: {
  bytes: Uint8Array;
  filename: string;
  mime: string;
}): Promise<void> {
  const { bytes, filename, mime } = args;
  const presign = await apiFetch<PresignResponse>("/api/v1/media/upload-url", {
    method: "POST",
    body: JSON.stringify({ mime, bytes: bytes.byteLength }),
  });
  // Copy into a fresh ArrayBuffer-backed view — fetch body refuses
  // SharedArrayBuffer-backed views (sql.js etc.).
  const buf = new Uint8Array(bytes.byteLength);
  buf.set(bytes);
  const put = await fetch(presign.url, {
    method: "PUT",
    headers: presign.headers,
    body: buf,
  });
  if (!put.ok) {
    const body = await put.text().catch(() => "");
    throw new Error(`Upload fehlgeschlagen (${put.status}). ${body}`.trim());
  }
  await apiFetch("/api/v1/media/register", {
    method: "POST",
    body: JSON.stringify({
      s3Key: presign.s3Key,
      mime,
      bytes: bytes.byteLength,
      filename: stripExtension(filename),
    }),
  });
}

/* ─────────────────────── helper: download to disk ───────────── */

const MIME_TO_EXT: Record<string, string> = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "text/markdown": "md",
  "text/plain": "txt",
  "application/json": "json",
};

function extensionFor(mime: string, filename: string): string | null {
  const fromMap = MIME_TO_EXT[mime];
  if (fromMap) return fromMap;
  const m = /\.([a-z0-9]+)$/i.exec(filename);
  return m && m[1] ? m[1].toLowerCase() : null;
}

async function saveBytesToDisk(args: {
  bytes: Uint8Array;
  filename: string;
  mime: string;
}): Promise<void> {
  const { bytes, filename, mime } = args;
  if (isTauri()) {
    const [{ save }, { writeFile }] = await Promise.all([
      import("@tauri-apps/plugin-dialog"),
      import("@tauri-apps/plugin-fs"),
    ]);
    const ext = extensionFor(mime, filename);
    const filters = ext ? [{ name: ext.toUpperCase(), extensions: [ext] }] : undefined;
    const picked = await save({ defaultPath: filename, filters });
    if (!picked) return;
    await writeFile(picked, bytes);
    return;
  }
  // Browser fallback.
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  const blob = new Blob([copy], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 4_000);
}

/* ─────────────────────────── hook ──────────────────────────── */

export function useExportSheet() {
  const [payload, setPayload] = useState<ExportSheetPayload | null>(null);
  const resolveRef = useRef<(() => void) | null>(null);
  // Internal print dialog — the sheet uses it for the "Drucken" action
  // so callers don't have to wire `usePrint()` themselves.
  const { openPrintDialog, dialog: printDialog } = usePrint();

  const openExportSheet = useCallback((next: ExportSheetPayload) => {
    return new Promise<void>((resolve) => {
      resolveRef.current = resolve;
      setPayload(next);
    });
  }, []);

  const close = useCallback(() => {
    setPayload(null);
    resolveRef.current?.();
    resolveRef.current = null;
  }, []);

  const sheet = (
    <>
      {payload && (
        <ExportSheet
          key={payload.filename + payload.bytes.byteLength}
          payload={payload}
          onClose={close}
          openPrintDialog={openPrintDialog}
        />
      )}
      {printDialog}
    </>
  );

  return { openExportSheet, sheet };
}

/* ─────────────────────────── sheet ─────────────────────────── */

interface ExportSheetProps {
  payload: ExportSheetPayload;
  onClose: () => void;
  openPrintDialog: ReturnType<typeof usePrint>["openPrintDialog"];
}

function ExportSheet({ payload, onClose, openPrintDialog }: ExportSheetProps) {
  const qc = useQueryClient();
  const { db, orgId } = useSync();
  const localAvailable = isLocalMediaAvailable() && Boolean(db) && Boolean(orgId);
  const shareAvailable = useMemo(() => isShareAvailable(), []);
  const canPrint = useMemo(
    () => payload.mime === "application/pdf" || payload.mime.startsWith("image/"),
    [payload.mime],
  );

  const [busy, setBusy] = useState<
    null | "download" | "save-local" | "save-cloud" | "share" | "print"
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Two-step "In Medien speichern": first picks lokal vs cloud, then runs.
  const [showSavePicker, setShowSavePicker] = useState(false);

  function fail(e: unknown, action: string) {
    console.error(`[export-sheet] ${action} failed:`, e);
    const name = e instanceof Error ? e.name : "";
    if (name === "AbortError") return; // user dismissed a native sheet
    setError(friendlyError(e, action));
  }

  async function runDownload() {
    if (busy) return;
    setBusy("download");
    setError(null);
    try {
      await saveBytesToDisk({
        bytes: payload.bytes,
        filename: payload.filename,
        mime: payload.mime,
      });
      setSuccess("Heruntergeladen.");
      window.setTimeout(onClose, 600);
    } catch (e) {
      fail(e, "download");
    } finally {
      setBusy(null);
    }
  }

  async function runSaveLocal() {
    if (busy) return;
    if (!db || !orgId) {
      setError("Lokale Datenbank nicht bereit.");
      return;
    }
    setBusy("save-local");
    setError(null);
    try {
      await importLocalMediaFromBytes(db, orgId, {
        filename: payload.filename,
        mime: payload.mime,
        bytes: payload.bytes,
      });
      await qc.invalidateQueries({ queryKey: ["media", "local", orgId] });
      setSuccess("In lokalen Medien gespeichert.");
      window.setTimeout(onClose, 700);
    } catch (e) {
      fail(e, "save-local");
    } finally {
      setBusy(null);
    }
  }

  async function runSaveCloud() {
    if (busy) return;
    setBusy("save-cloud");
    setError(null);
    try {
      await uploadBytesToCloud({
        bytes: payload.bytes,
        filename: payload.filename,
        mime: payload.mime,
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["media", "list"] }),
        qc.invalidateQueries({ queryKey: ["me", "storage"] }),
      ]);
      setSuccess("In Cloud-Medien gespeichert.");
      window.setTimeout(onClose, 700);
    } catch (e) {
      fail(e, "save-cloud");
    } finally {
      setBusy(null);
    }
  }

  /**
   * Share runs synchronously after a click event so the browser's
   * transient user-activation is still valid when `navigator.share`
   * fires — see export-menu.tsx for the same constraint.
   */
  function runShare() {
    if (busy) return;
    setBusy("share");
    setError(null);
    try {
      const file = new File([payload.bytes.buffer as ArrayBuffer], payload.filename, {
        type: payload.mime,
      });
      shareFile(file)
        .then(() => {
          setSuccess("Geteilt.");
          window.setTimeout(onClose, 600);
        })
        .catch((e) => fail(e, "share"))
        .finally(() => setBusy(null));
    } catch (e) {
      fail(e, "share");
      setBusy(null);
    }
  }

  async function runPrint() {
    if (busy) return;
    setBusy("print");
    setError(null);
    try {
      const blob = new Blob([payload.bytes.buffer as ArrayBuffer], {
        type: payload.mime,
      });
      const blobUrl = URL.createObjectURL(blob);
      try {
        if (payload.mime === "application/pdf") {
          // pdf-lib gives us the page count cheaply — needed so the
          // print dialog's "Aktuelle Seite" option has a sensible default.
          const doc = await PDFDocument.load(payload.bytes, {
            ignoreEncryption: true,
          });
          const totalPages = doc.getPageCount();
          // Close the sheet first so its overlay doesn't dim the print
          // dialog stacked on top.
          onClose();
          await openPrintDialog({
            title: payload.title ?? payload.filename,
            job: {
              kind: "pdf",
              src: blobUrl,
              currentPage: 1,
              totalPages,
            },
          });
        } else if (payload.mime.startsWith("image/")) {
          onClose();
          await openPrintDialog({
            title: payload.title ?? payload.filename,
            job: { kind: "image", src: blobUrl },
          });
        } else {
          throw new Error("Drucken für diesen Dateityp nicht unterstützt.");
        }
      } finally {
        // Give the print iframe time to load the blob before we revoke.
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      }
    } catch (e) {
      fail(e, "print");
    } finally {
      setBusy(null);
    }
  }

  const fileSize = formatBytes(payload.bytes.byteLength);
  const heading = payload.title ?? "Export bereit";

  /* ─────────────────────────── render ─────────────────────── */

  return (
    <div
      className="fixed inset-0 z-300 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
      onClick={busy ? undefined : onClose}
      role="presentation"
    >
      <div
        className="glass-floating w-full max-w-md rounded-3xl border border-white/10 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-sheet-title"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-pitch-lime/15 text-pitch-lime">
              <FileText className="size-5" />
            </div>
            <div className="min-w-0">
              <h2 id="export-sheet-title" className="text-body-md font-semibold leading-tight">
                {heading}
              </h2>
              <p
                className="mt-0.5 truncate text-[12px]"
                style={{ color: "var(--color-on-surface-variant)" }}
                title={payload.filename}
              >
                {payload.filename} · {fileSize}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={busy ? undefined : onClose}
            aria-label="Schließen"
            disabled={busy !== null}
            className="rounded-lg p-1.5 text-white/70 hover:bg-white/10 disabled:opacity-40"
          >
            <X className="size-4" />
          </button>
        </div>

        {!showSavePicker && (
          <div className="grid grid-cols-2 gap-2">
            <ActionCard
              label="Herunterladen"
              hint="Auf Gerät speichern"
              icon={<Download className="size-5" />}
              busy={busy === "download"}
              disabled={busy !== null}
              onClick={() => void runDownload()}
            />
            <ActionCard
              label="In Medien speichern"
              hint={localAvailable ? "Lokal oder Cloud" : "Cloud-Mediathek"}
              icon={<Cloud className="size-5" />}
              busy={busy === "save-local" || busy === "save-cloud"}
              disabled={busy !== null}
              onClick={() => {
                if (!localAvailable) {
                  void runSaveCloud();
                  return;
                }
                setShowSavePicker(true);
              }}
            />
            {shareAvailable && (
              <ActionCard
                label="Teilen"
                hint="System-Sharesheet"
                icon={<Share2 className="size-5" />}
                busy={busy === "share"}
                disabled={busy !== null}
                onClick={runShare}
              />
            )}
            {canPrint && (
              <ActionCard
                label="Drucken"
                hint="Druckdialog öffnen"
                icon={<Printer className="size-5" />}
                busy={busy === "print"}
                disabled={busy !== null}
                onClick={() => void runPrint()}
              />
            )}
          </div>
        )}

        {showSavePicker && (
          <div className="space-y-2">
            <p className="mb-1 text-[12px]" style={{ color: "var(--color-on-surface-variant)" }}>
              In welche Mediathek speichern?
            </p>
            <ActionCard
              label="Lokal auf diesem Gerät"
              hint="Bleibt offline, kein Cloud-Speicher belegt"
              icon={<HardDrive className="size-5" />}
              busy={busy === "save-local"}
              disabled={busy !== null}
              onClick={() => void runSaveLocal()}
              wide
            />
            <ActionCard
              label="Cloud-Mediathek"
              hint="Über alle Geräte synchronisiert"
              icon={<Cloud className="size-5" />}
              busy={busy === "save-cloud"}
              disabled={busy !== null}
              onClick={() => void runSaveCloud()}
              wide
            />
            <button
              type="button"
              onClick={() => setShowSavePicker(false)}
              disabled={busy !== null}
              className="mt-1 w-full rounded-xl px-3 py-2 text-[12px] font-semibold text-white/70 hover:bg-white/5 disabled:opacity-40"
            >
              Zurück
            </button>
          </div>
        )}

        {(error || success) && (
          <div className="mt-3 text-[12px]">
            {error && (
              <p style={{ color: "#ffb4b4" }} role="alert">
                {error}
              </p>
            )}
            {success && !error && <p style={{ color: "var(--color-pitch-lime)" }}>{success}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────── action card ─────────────────────── */

function ActionCard({
  label,
  hint,
  icon,
  busy,
  disabled,
  onClick,
  wide,
}: {
  label: string;
  hint: string;
  icon: React.ReactNode;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
  wide?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`group flex items-center gap-3 rounded-2xl border border-white/10 px-3 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${wide ? "w-full" : ""}`}
      style={{ background: "rgba(255,255,255,0.04)" }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = "rgba(178,255,89,0.08)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(255,255,255,0.04)";
      }}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-pitch-lime/15 text-pitch-lime">
        {busy ? <Loader2 className="size-5 animate-spin" /> : icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold" style={{ color: "#f4f5f3" }}>
          {label}
        </span>
        <span
          className="block truncate text-[11px]"
          style={{ color: "var(--color-on-surface-variant)" }}
        >
          {hint}
        </span>
      </span>
    </button>
  );
}

/* ─────────────────────── small utils ─────────────────────── */

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(2)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

function friendlyError(err: unknown, action: string): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (action === "share") {
    if (/NotAllowed/i.test(msg)) return "Teilen wurde vom Browser nicht erlaubt.";
    if (/canShare|DataError/i.test(msg)) return "Diese Datei kann hier nicht geteilt werden.";
    return "Teilen fehlgeschlagen.";
  }
  if (action === "save-cloud") {
    if (/413/.test(msg)) return "Cloud-Speicher voll.";
    if (/Upload/i.test(msg)) return msg;
    return "Speichern in der Cloud fehlgeschlagen.";
  }
  if (action === "save-local") {
    return "Lokales Speichern fehlgeschlagen.";
  }
  if (action === "print") {
    return "Drucken fehlgeschlagen.";
  }
  return "Speichern fehlgeschlagen.";
}
